import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { StopwatchStore } from '@/configs/store/StopwatchStore';
import { Stopwatch } from '@/types';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { parseTimeExpression } from '@/utils/parseTimeInput';

import { calcElapsedSeconds } from './utils';

// ── Timer tick ──

export const useElapsedSeconds = (stopwatch: Stopwatch, clockOffset: number): number => {
  const [displaySeconds, setDisplaySeconds] = useState(() =>
    calcElapsedSeconds(stopwatch, clockOffset),
  );

  const isRunning = stopwatch.status === 'running';

  useEffect(() => {
    setDisplaySeconds(calcElapsedSeconds(stopwatch, clockOffset));

    if (!isRunning) return;

    const interval = setInterval(() => {
      setDisplaySeconds(calcElapsedSeconds(stopwatch, clockOffset));
    }, 1000);

    return () => clearInterval(interval);
  }, [stopwatch, isRunning, clockOffset]);

  return displaySeconds;
};

// ── Inline time editor ──

type TimeEditorState = {
  editing: boolean;
  timeInput: string;
  setTimeInput: (value: string) => void;
  parsedTimeMinutes: number | null;
  isTimeValid: boolean;
  handleStartEdit: () => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
};

export const useTimeEditor = (stopwatch: Stopwatch, store: StopwatchStore): TimeEditorState => {
  const [editing, setEditing] = useState(false);
  const [timeInput, setTimeInput] = useState('');
  const isRunning = stopwatch.status === 'running';

  const parsedTimeMinutes = useMemo(() => parseTimeExpression(timeInput), [timeInput]);
  const isTimeValid = parsedTimeMinutes !== null && parsedTimeMinutes > 0;

  const handleStartEdit = useCallback(() => {
    if (isRunning) return;

    const minutes = Math.round(stopwatch.accumulatedSeconds / 60);
    setTimeInput(formatDurationRussian(minutes) || '0м');
    setEditing(true);
  }, [isRunning, stopwatch.accumulatedSeconds]);

  const handleSave = useCallback(async () => {
    if (!isTimeValid || parsedTimeMinutes === null) return;

    setEditing(false);
    await store.updateStopwatch(stopwatch.id, { accumulatedSeconds: parsedTimeMinutes * 60 });
  }, [isTimeValid, parsedTimeMinutes, stopwatch.id, store]);

  const handleCancel = useCallback(() => {
    setEditing(false);
    setTimeInput('');
  }, []);

  return {
    editing,
    timeInput,
    setTimeInput,
    parsedTimeMinutes,
    isTimeValid,
    handleStartEdit,
    handleSave,
    handleCancel,
  };
};

// ── Comment with debounced save ──

type DebouncedCommentState = {
  comment: string;
  setComment: (value: string) => void;
  handleBlur: () => void;
};

export const useDebouncedComment = (
  stopwatch: Stopwatch,
  store: StopwatchStore,
): DebouncedCommentState => {
  const [comment, setComment] = useState(stopwatch.comment);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setComment(stopwatch.comment);
  }, [stopwatch.comment]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleBlur = useCallback(() => {
    if (comment !== stopwatch.comment) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(async () => {
        await store.updateStopwatch(stopwatch.id, { comment });
      }, 300);
    }
  }, [comment, stopwatch.comment, stopwatch.id, store]);

  return { comment, setComment, handleBlur };
};

// ── Log-at editor ──

type LogAtEditorState = {
  editing: boolean;
  dateValue: string;
  timeValue: string;
  setDateValue: (value: string) => void;
  setTimeValue: (value: string) => void;
  isValid: boolean;
  handleStartEdit: () => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
};

export const useLogAtEditor = (stopwatch: Stopwatch, store: StopwatchStore): LogAtEditorState => {
  const [editing, setEditing] = useState(false);
  const [dateValue, setDateValue] = useState('');
  const [timeValue, setTimeValue] = useState('');

  const isValid = dateValue !== '' && timeValue !== '';

  const handleStartEdit = useCallback(() => {
    const dt = DateTime.fromISO(stopwatch.logAt);
    setDateValue(dt.toFormat('yyyy-MM-dd'));
    setTimeValue(dt.toFormat('HH:mm'));
    setEditing(true);
  }, [stopwatch.logAt]);

  const handleSave = useCallback(async () => {
    if (!isValid) return;

    const dt = DateTime.fromISO(`${dateValue}T${timeValue}`);

    if (!dt.isValid) return;

    setEditing(false);
    await store.updateStopwatch(stopwatch.id, { logAt: dt.toISO() });
  }, [isValid, dateValue, timeValue, stopwatch.id, store]);

  const handleCancel = useCallback(() => {
    setEditing(false);
  }, []);

  return {
    editing,
    dateValue,
    timeValue,
    setDateValue,
    setTimeValue,
    isValid,
    handleStartEdit,
    handleSave,
    handleCancel,
  };
};
